/**
 * `/access` — who can open FP&A and with which role (`GET/POST/PATCH/DELETE /v1/access`). Visible to
 * admins and managers (`/v1/me.permissions.canManageAccess`); only admins can grant or revoke admin.
 */
import { useCallback, useMemo, useState } from 'react'

import { errorMessage, isApiError } from '@/api/client'
import type { AccessRole, AccessUser } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Badge } from '@/components/ui/Badge'
import { Notice } from '@/components/ui/Notice'
import { Panel } from '@/components/ui/Panel'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/StateMessage'
import { useMe } from '@/features/auth/MeContext'
import layout from '@/features/runway/components/Layout.module.css'
import { PageHead } from '@/features/runway/components/PageHead'

import styles from './Access.module.css'
import { AccessTable } from './AccessTable'
import { AddPersonDialog } from './AddPersonDialog'
import { RemoveAccessDialog } from './RemoveAccessDialog'
import { ROLE_HELP, ROLE_LABEL } from './rules'
import { useAccessUsers, useChangeRole } from './useAccess'

const ROLES: AccessRole[] = ['viewer', 'manager', 'admin']

export function AccessPage() {
  const me = useMe()
  const allowed = me.permissions.canManageAccess
  const users = useAccessUsers(allowed)
  const changeRole = useChangeRole()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<AccessUser | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)

  const counts = useMemo(() => {
    const list = users.data ?? []
    const active = list.filter((u) => u.status === 'active').length
    return { active, removed: list.length - active }
  }, [users.data])

  const openAdd = useCallback(() => {
    setFlash(null)
    setAdding(true)
  }, [])
  const closeAdd = useCallback(() => setAdding(false), [])
  const handleAdded = useCallback((user: AccessUser) => {
    setAdding(false)
    setFlash(`${user.name} can now sign in to FP&A as ${ROLE_LABEL[user.role].toLowerCase()}.`)
  }, [])

  const handleRemove = useCallback((user: AccessUser) => {
    setFlash(null)
    setRemoving(user)
  }, [])
  const closeRemove = useCallback(() => setRemoving(null), [])
  const handleRemoved = useCallback((user: AccessUser) => {
    setRemoving(null)
    setFlash(`Removed access for ${user.name}.`)
  }, [])

  const { mutate: mutateRole } = changeRole
  const handleRoleChange = useCallback(
    (user: AccessUser, role: AccessRole) => {
      setFlash(null)
      setRoleError(null)
      mutateRole(
        { id: user.id, role },
        {
          onSuccess: () => setFlash(`${user.name} is now ${ROLE_LABEL[role].toLowerCase()}.`),
          onError: (error) => setRoleError(errorMessage(error, `Could not change ${user.name}'s role.`)),
        },
      )
    },
    [mutateRole],
  )
  const handleRetry = useCallback(() => {
    void users.refetch()
  }, [users])

  if (!allowed || (isApiError(users.error) && users.error.isForbidden)) {
    return (
      <div className={layout.view}>
        <PageHead title="Access" sub="Who can open FP&A and what they can do." badge={null} />
        <ErrorState>You need the manager or admin role to manage access. Ask an FPA admin if you need it.</ErrorState>
      </div>
    )
  }

  const busyId = changeRole.isPending ? (changeRole.variables?.id ?? null) : null

  return (
    <div className={layout.view}>
      <PageHead
        title="Access"
        sub="Who can open FP&A and what they can do. People sign in with their RBI Microsoft account."
        badge={
          <button type="button" className={styles.primary} onClick={openAdd}>
            <ICONS.plus size={14} />
            Add person
          </button>
        }
      />

      {flash ? (
        <div className={styles.flash} role="status">
          <ICONS.check size={14} className={styles.flashIcon} />
          {flash}
        </div>
      ) : null}
      {roleError ? <Notice tone="warn">{roleError}</Notice> : null}
      {!me.permissions.canGrantAdmin ? (
        <Notice tone="info">
          As a manager you can add, change and remove viewers and managers. Only admins can grant admin.
        </Notice>
      ) : null}

      <Panel
        title="People"
        aside={
          users.data ? (
            <Badge tone="neutral" small>
              {counts.active} active{counts.removed ? ` · ${counts.removed} removed` : ''}
            </Badge>
          ) : undefined
        }
      >
        {users.isPending ? (
          <Skeleton label="Loading people" />
        ) : users.error ? (
          <ErrorState>
            {errorMessage(users.error, 'Could not load the people with access.')}
            <div className={styles.stateActions}>
              <button type="button" className={styles.secondary} onClick={handleRetry}>
                <ICONS.refresh size={13} />
                Try again
              </button>
            </div>
          </ErrorState>
        ) : users.data.length === 0 ? (
          <EmptyState>No one has been added yet. Use “Add person” to give someone access.</EmptyState>
        ) : (
          <AccessTable
            users={users.data}
            me={me}
            busyId={busyId}
            onRoleChange={handleRoleChange}
            onRemove={handleRemove}
          />
        )}
      </Panel>

      <Panel title="Roles">
        <ul className={styles.roles}>
          {ROLES.map((role) => (
            <li key={role}>
              <Badge tone={role === 'admin' ? 'accent' : role === 'manager' ? 'good' : 'neutral'} small>
                {ROLE_LABEL[role]}
              </Badge>
              <p className={styles.roleHelp}>{ROLE_HELP[role]}</p>
            </li>
          ))}
        </ul>
      </Panel>

      {adding ? <AddPersonDialog me={me} onClose={closeAdd} onAdded={handleAdded} /> : null}
      {removing ? <RemoveAccessDialog user={removing} onClose={closeRemove} onRemoved={handleRemoved} /> : null}
    </div>
  )
}
